import { useCallback, useEffect, useMemo, useState } from "react";
import { useUserScopedSettings } from "./useUserScopedSettings";

interface SelectionSettings extends Record<string, unknown> {
  selectedId: string;
}

const NOTHING_REMEMBERED: SelectionSettings = { selectedId: "" };

/** What the user picked in this session, as opposed to what was remembered for them. */
interface Choice {
  /** `null` means they cleared the selection. */
  id: string | null;
}

export interface RememberedSelection<TOption> {
  selected: TOption | null;
  setSelected: (next: TOption | null) => void;
}

/**
 * Remembers which of `options` the user picked, per user and per scope.
 *
 * Written once and shared, because the rules are easy to get subtly wrong: the remembered
 * choice has to apply even when it arrives after the options do; landing on the first option
 * is a fallback and must not be stored as if the user had picked it; clearing the selection has
 * to stay cleared; and the held option has to be re-resolved from each refreshed list, or one
 * edited elsewhere stays stale until a reload.
 *
 * @param scope - Names the selection, e.g. `faultMonitor.preset`. Must be stable.
 * @param options - The options to choose from, in display order.
 */
export function useRememberedSelection<TOption extends { id: string }>(
  scope: string,
  options: TOption[],
): RememberedSelection<TOption> {
  const { settings, setSettings } = useUserScopedSettings<SelectionSettings>(
    scope,
    NOTHING_REMEMBERED,
  );

  /** `null` while the user has not touched the selection, in which case the record applies. */
  const [choice, setChoice] = useState<Choice | null>(null);

  const wantedId = choice === null ? settings.selectedId : choice.id;

  // Derived rather than copied into state, so a late-arriving record still applies and the held
  // option always comes from the current list.
  const selected = useMemo(() => {
    if (options.length === 0) {
      return null;
    }
    if (choice !== null && choice.id === null) {
      // The user cleared the selection; leave it cleared.
      return null;
    }

    const wanted = wantedId ? options.find(option => option.id === wantedId) : undefined;
    if (wanted) {
      return wanted;
    }

    // Fall back to the first option only while the user has not chosen: after an explicit
    // choice, a vanished option must not silently become a different one.
    return choice === null ? options[0] : null;
  }, [choice, options, wantedId]);

  const setSelected = useCallback((next: TOption | null) => {
    setChoice({ id: next === null ? null : next.id });
  }, []);

  useEffect(() => {
    const chosenId = choice?.id;
    if (chosenId === undefined || chosenId === null) {
      return;
    }
    setSettings(current =>
      current.selectedId === chosenId ? current : { ...current, selectedId: chosenId },
    );
  }, [choice, setSettings]);

  return { selected, setSelected };
}
